import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup, Label, Input, Button } from 'reactstrap';
import { useTenantContext } from '../../auth/useTenantContext';
import { useResetPassword, type AdminUser } from '../../api/adminUsers';
import { PasswordGenerator } from '../tenants/PasswordGenerator';

interface Props { user: AdminUser; onClose: () => void; inline?: boolean }

export function ResetPasswordModal({ user, onClose, inline }: Props) {
  const { t } = useTranslation(['user', 'common']);
  const { token, tenantCode } = useTenantContext();
  const mut = useResetPassword(token, tenantCode);
  const [pwd, setPwd] = useState('');
  const [error, setError] = useState<string | null>(null);

  const userLabel = user.display_name ?? user.email ?? t('user:page.fallbackLabel', { id: user.id });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await mut.mutateAsync({ userId: user.id, temporary_password: pwd });
      onClose();
    } catch (err: unknown) {
      const e = err as { body?: { detail?: string }; message?: string };
      setError(e?.body?.detail ?? e?.message ?? t('user:resetPassword.errorGeneric'));
    }
  };

  const form = (
    <form
      onSubmit={submit}
      style={inline ? { display: 'flex', flexDirection: 'column', gap: 12 } : {
        background: 'var(--vp-surface, #fff)', padding: 16, borderRadius: 8, minWidth: 360,
        display: 'flex', flexDirection: 'column', gap: 12,
      }}
    >
      <h5 className="fs-15 mb-0">{t('user:resetPassword.title', { label: userLabel })}</h5>
      <p className="small text-muted mb-0">
        {t('user:resetPassword.helpText')}
      </p>
      <FormGroup className="mb-0">
        <Label for="reset-pwd" className="mb-1">{t('user:resetPassword.label')}</Label>
        <div className="d-flex gap-2">
          <Input
            id="reset-pwd" aria-label={t('user:resetPassword.ariaLabel')} type="text"
            value={pwd} onChange={(e) => setPwd(e.target.value)}
            minLength={12} required
            style={{ flex: 1 }}
          />
          <PasswordGenerator onGenerated={setPwd} value={pwd} />
        </div>
      </FormGroup>
      {error && <p role="alert" className="text-danger small mb-0">{error}</p>}
      <div className="d-flex justify-content-end gap-2">
        <Button type="button" color="light" size="sm" onClick={onClose}>{t('common:actions.cancel')}</Button>
        <Button type="submit" color="primary" size="sm">{t('user:resetPassword.submit')}</Button>
      </div>
    </form>
  );

  if (inline) return form;

  return (
    <div
      role="dialog" aria-modal="true"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {form}
    </div>
  );
}
